// Saídas: retirada de material para uso, vários produtos de uma vez (tudo ou nada). Saída maior
// que o saldo é recusada pelo backend; se o parâmetro permitir_saldo_negativo estiver ligado,
// a tela pede justificativa e reenvia. Pensada para o celular.
import { useCallback, useEffect, useMemo, useState } from "react";
import { CheckCircle2, Plus, Trash2, XCircle } from "lucide-react";
import { getEstoqueMovimentacoes, getEstoquePosicao, getEstoqueProdutos, getRoleFromToken, postEstoqueCancelarMovimento, postEstoqueSaidas } from "../../api";
import { permissoesEstoque } from "../../constants/estoque";
import EstoqueLayout from "./EstoqueLayout";
import {
  AvisoSemAbertura, JustificativaSaldoNegativo, ModalCancelar, ProdutoSelect, ehSaldoNegativo, formatData, hojeISO,
  mensagemErro, useContextoEstoque,
} from "./comum";
import { AvisoErro, Campo, NumeroInput, btnStyle, formatBRL, formatQtd, inputStyle, palette, parseNumero } from "./ui";

const DESTINOS = ["Cozinha", "Banheiros", "Recepção", "Sala de reunião", "Diretoria", "Limpeza geral"];

let _seq = 0;
const novaLinha = () => ({ chave: ++_seq, produto_id: null, quantidade: "" });

const diasAtras = (n) => {
  const d = new Date();
  d.setDate(d.getDate() - n);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
};

export default function SaidasView() {
  const [isLight, setIsLight] = useState(true);
  const C = palette(isLight);
  const perm = permissoesEstoque(getRoleFromToken());
  const { contexto, erro: erroContexto } = useContextoEstoque();
  const [produtos, setProdutos] = useState([]);
  const [saldos, setSaldos] = useState({});
  const [form, setForm] = useState({ data: hojeISO(), responsavel_nome: "", destino: "" });
  const [linhas, setLinhas] = useState([novaLinha()]);
  const [justificativa, setJustificativa] = useState("");
  const [erro, setErro] = useState(null);
  const [sucesso, setSucesso] = useState("");
  const [salvando, setSalvando] = useState(false);
  const [historico, setHistorico] = useState([]);
  const [cancelando, setCancelando] = useState(null);

  const carregarSaldos = useCallback(() => getEstoquePosicao().then((p) => {
    setSaldos(Object.fromEntries(p.itens.map((i) => [i.produto_id, Number(i.quantidade)])));
  }), []);
  const carregarHistorico = useCallback(() => getEstoqueMovimentacoes({ tipo: "SAIDA", inicio: diasAtras(30) }).then(setHistorico), []);

  useEffect(() => {
    getEstoqueProdutos(true).then(setProdutos).catch((e) => setErro(e));
    carregarSaldos().catch((e) => setErro(e));
    carregarHistorico().catch((e) => setErro(e));
  }, [carregarSaldos, carregarHistorico]);
  useEffect(() => {
    if (contexto) setForm((f) => ({ ...f, responsavel_nome: f.responsavel_nome || contexto.usuario_nome }));
  }, [contexto]);

  const solicitadoPorProduto = useMemo(() => {
    const m = {};
    for (const l of linhas) if (l.produto_id) m[l.produto_id] = (m[l.produto_id] || 0) + (Number(parseNumero(l.quantidade)) || 0);
    return m;
  }, [linhas]);

  const setLinha = (chave, campo, valor) => setLinhas((ls) => ls.map((l) => (l.chave === chave ? { ...l, [campo]: valor } : l)));

  async function registrar() {
    setSucesso("");
    const preenchidas = linhas.filter((l) => l.produto_id || l.quantidade);
    if (!preenchidas.length) { setErro(new Error("Informe pelo menos um produto.")); return; }
    for (const [idx, l] of preenchidas.entries()) {
      const q = parseNumero(l.quantidade);
      if (!l.produto_id || !q || Number(q) <= 0) { setErro(new Error(`Linha ${idx + 1}: escolha o produto e uma quantidade maior que zero.`)); return; }
    }
    setSalvando(true);
    try {
      const criadas = await postEstoqueSaidas({
        data: form.data,
        responsavel_nome: form.responsavel_nome.trim() || null,
        destino: form.destino.trim() || null,
        justificativa_saldo_negativo: justificativa.trim() || null,
        itens: preenchidas.map((l) => ({ produto_id: l.produto_id, quantidade: parseNumero(l.quantidade) })),
      });
      const valor = criadas.reduce((s, m) => s - Number(m.valor_total), 0);
      setSucesso(`${criadas.length} saída(s) registrada(s) · ${formatBRL(valor)}`);
      setLinhas([novaLinha()]);
      setJustificativa("");
      setErro(null);
      carregarSaldos();
      carregarHistorico();
    } catch (e) {
      setErro(e);
    } finally {
      setSalvando(false);
    }
  }

  return (
    <EstoqueLayout titulo="Saídas" isLight={isLight} setIsLight={setIsLight}>
      {erroContexto && <AvisoErro C={C}>{erroContexto}</AvisoErro>}
      {!contexto ? null : !contexto.abertura_concluida ? <AvisoSemAbertura C={C} /> : (
        <div style={{ maxWidth: 720 }}>
          {sucesso && (
            <div style={{ display: "flex", gap: 8, alignItems: "center", background: `${C.success}18`, border: `1px solid ${C.success}`, color: C.success, borderRadius: 8, padding: "10px 12px", fontSize: 14, fontWeight: 600, marginBottom: 12 }}>
              <CheckCircle2 size={18} /> {sucesso}
            </div>
          )}
          <AvisoErro C={C} onClose={() => setErro(null)}>{erro && mensagemErro(erro)}</AvisoErro>
          <JustificativaSaldoNegativo C={C} erro={erro} valor={justificativa} onChange={setJustificativa} />

          <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 12, padding: 14, marginBottom: 12, display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))", gap: 12 }}>
            <Campo label="Data" C={C} obrigatorio>
              <input type="date" value={form.data} min={contexto.data_abertura || undefined} max={hojeISO()} onChange={(e) => setForm({ ...form, data: e.target.value })} style={inputStyle(C)} />
            </Campo>
            <Campo label="Responsável" C={C}>
              <input value={form.responsavel_nome} onChange={(e) => setForm({ ...form, responsavel_nome: e.target.value })} style={inputStyle(C)} />
            </Campo>
            <Campo label="Destino / setor" C={C}>
              <input value={form.destino} list="estoque-destinos" onChange={(e) => setForm({ ...form, destino: e.target.value })} placeholder="Opcional" style={inputStyle(C)} />
              <datalist id="estoque-destinos">{DESTINOS.map((d) => <option key={d} value={d} />)}</datalist>
            </Campo>
          </div>

          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            {linhas.map((l, idx) => {
              const produto = produtos.find((p) => p.id === l.produto_id);
              const saldo = l.produto_id ? saldos[l.produto_id] ?? 0 : null;
              const excede = l.produto_id && solicitadoPorProduto[l.produto_id] > saldo + 1e-9;
              return (
                <div key={l.chave} style={{ background: C.card, border: `1px solid ${excede ? C.danger : C.border}`, borderRadius: 12, padding: 12 }}>
                  <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <ProdutoSelect C={C} produtos={produtos} value={l.produto_id} onChange={(id) => setLinha(l.chave, "produto_id", id)} aria-label={`Produto da linha ${idx + 1}`} />
                    </div>
                    {linhas.length > 1 && (
                      <button onClick={() => setLinhas((ls) => ls.filter((x) => x.chave !== l.chave))} aria-label="Remover linha" style={{ width: 42, height: 42, borderRadius: 8, border: `1px solid ${C.border}`, background: "transparent", color: C.danger, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                        <Trash2 size={16} />
                      </button>
                    )}
                  </div>
                  <div style={{ display: "flex", gap: 8, alignItems: "center", marginTop: 8 }}>
                    <NumeroInput C={C} value={l.quantidade} onChange={(v) => setLinha(l.chave, "quantidade", v)} placeholder="Quantidade" aria-label={`Quantidade da linha ${idx + 1}`} style={{ width: 130, fontSize: 16, fontWeight: 700 }} />
                    <span style={{ fontSize: 13, color: C.muted, fontWeight: 600 }}>{produto?.unidade_padrao_sigla}</span>
                    {saldo !== null && (
                      <span style={{ marginLeft: "auto", fontSize: 12, color: excede ? C.danger : C.muted, fontWeight: excede ? 700 : 500 }}>
                        Saldo: {formatQtd(saldo)} {produto?.unidade_padrao_sigla}{excede ? " · insuficiente" : ""}
                      </span>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
          <div style={{ display: "flex", gap: 8, marginTop: 12, flexWrap: "wrap" }}>
            <button onClick={() => setLinhas((ls) => [...ls, novaLinha()])} style={btnStyle(C.brand, true)}><Plus size={15} /> Mais um produto</button>
            <button onClick={registrar} disabled={salvando} style={{ ...btnStyle(C.danger), flex: "1 1 200px", opacity: salvando ? 0.6 : 1 }}>
              {salvando ? "Registrando..." : ehSaldoNegativo(erro) && erro.data.permitido_com_justificativa ? "Registrar mesmo assim" : "Registrar saída"}
            </button>
          </div>

          <h3 style={{ fontSize: 14, margin: "28px 0 8px" }}>Saídas dos últimos 30 dias</h3>
          <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
            {historico.length === 0 && <div style={{ fontSize: 13, color: C.muted }}>Nenhuma saída.</div>}
            {historico.map((m) => (
              <div key={m.id} style={{ display: "flex", alignItems: "center", gap: 10, background: C.card, border: `1px solid ${C.border}`, borderRadius: 10, padding: "8px 12px", opacity: m.status === "CANCELADA" ? 0.5 : 1 }}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 13, fontWeight: 600, textDecoration: m.status === "CANCELADA" ? "line-through" : "none" }}>
                    {formatQtd(-m.quantidade)} {m.unidade_sigla} · {m.produto_nome}
                  </div>
                  <div style={{ fontSize: 11, color: C.muted }}>
                    {formatData(m.data)} · {m.responsavel_nome || "—"}{m.destino ? ` · ${m.destino}` : ""} · {formatBRL(-m.valor_total)}
                    {m.status === "CANCELADA" && ` · cancelada: ${m.motivo_cancelamento}`}
                  </div>
                </div>
                {perm.financeiro && m.status === "ATIVA" && (
                  <button onClick={() => setCancelando(m)} aria-label="Cancelar saída" title="Cancelar saída" style={{ background: "none", border: "none", color: C.muted, cursor: "pointer", padding: 6, display: "flex" }}>
                    <XCircle size={18} />
                  </button>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
      {cancelando && (
        <ModalCancelar C={C} titulo="Cancelar saída" onClose={() => setCancelando(null)}
          descricao={`${formatQtd(-cancelando.quantidade)} ${cancelando.unidade_sigla} de ${cancelando.produto_nome} em ${formatData(cancelando.data)} voltam para o saldo.`}
          onConfirmar={async (dados) => { await postEstoqueCancelarMovimento(cancelando.id, dados); setCancelando(null); carregarSaldos(); carregarHistorico(); }} />
      )}
    </EstoqueLayout>
  );
}
